import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSiteContent } from "../../context/SiteContentContext";
import { thaiDate } from "../../components/site/siteText";
import { apiUrl, trackAnnouncement } from "../../services/newspaperApi";
import ContactAdminBox from "./ContactAdminBox";
import PaymentBox from "./PaymentBox";
import "./newspaper.css";
import { formatDate } from "../../utils/dateFormat";

const BADGE = { submitted: "status-pending", paid: "status-revision", approved: "status-approved", rejected: "status-rejected" };

function StatusPage() {
  const { siteContent } = useSiteContent();
  const c = siteContent.statusPage || {};
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [phone, setPhone] = useState("");
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "ติดตามสถานะประกาศ | Maneenopkao Group";
  }, []);

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setItem(null);
    setBusy(true);
    try {
      const res = await trackAnnouncement(code.trim(), phone.trim());
      setItem(res.item);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const published = item?.published;

  return (
    <main id="main" className="container dashboard-page">
      <Link className="breadcrumb" to="/newspaper">← หนังสือพิมพ์</Link>
      <div className="section-heading">
        <div>
          <p className="eyebrow">{c.eyebrow}</p>
          <h1>{c.title}</h1>
          <p className="dashboard-subtitle">{c.description}</p>
        </div>
      </div>

      <div className="submission-layout">
        <div className="submission-form-card">
          <form className="site-form" onSubmit={lookup}>
            {error && <p className="form-error" role="alert">{error}</p>}
            <div className="form-row">
              <label>
                เลขติดตาม / เลขรายการชำระเงิน <span className="required">*</span>
                <input required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="เช่น MNAB23CD หรือ ODAB23CD" autoComplete="off" />
              </label>
              <label>
                เบอร์โทรที่ใช้ตอนส่ง <span className="required">*</span>
                <input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
              </label>
            </div>
            <button className="button dark form-submit" type="submit" disabled={busy}>
              {busy ? "กำลังค้นหา..." : "ดูสถานะ"}{" "}
              <span>↗</span>
            </button>
          </form>
        </div>

        <aside className="submit-guidance">
          <p className="eyebrow">STATUS GUIDE</p>
          <h3>ความหมายของสถานะ</h3>
          <ol>
            <li>รอชำระเงิน — ชำระด้วย QR พร้อมเพย์ / โอนเข้าบัญชี แล้วแนบสลิปที่หน้านี้</li>
            <li>ชำระแล้ว รอตรวจทาน — ทีมกำลังตรวจเนื้อหา</li>
            <li>อนุมัติแล้ว — กำหนดวันลงฉบับเรียบร้อย</li>
          </ol>
        </aside>
      </div>

      {item && (
        <div className="my-records" style={{ marginTop: 40 }}>
          <article>
            <div className="record-topline">
              <span className={`status-badge ${published ? "status-published" : BADGE[item.status] || ""}`}>
                {published ? "เผยแพร่แล้ว" : item.statusLabel}
              </span>
              <small>{item.trackingCode} · ส่งเมื่อ {formatDate(item.createdAt)}</small>
            </div>
            {item.locked ? (
              <>
                <h2>{item.status === "rejected" ? "ประกาศของคุณยังไม่ได้รับอนุมัติ" : "ประกาศของคุณอยู่ระหว่างดำเนินการ"}</h2>
                <p>{c.lockedText}</p>
                {item.order && (
                  <div className="basket-box">
                    <p className="eyebrow">รายการชำระเงิน {item.order.orderCode} · {item.order.count} เรื่อง</p>
                    <ol>
                      {item.order.items.map((i) => (
                        <li key={i.trackingCode}><strong>{i.trackingCode}</strong> · {i.title} · {i.statusLabel}</li>
                      ))}
                    </ol>
                    {item.order.copies > 0 && <p>หนังสือพิมพ์ฉบับพิมพ์ {item.order.copies} เล่ม</p>}
                    <p className="basket-total">ยอดรวม <strong>{Number(item.order.total).toLocaleString("th-TH")} บาท</strong></p>
                  </div>
                )}
                {item.status === "submitted" && (
                  item.order ? (
                    <PaymentBox key={item.order.orderCode} trackingCode={item.order.orderCode} phone={phone.trim()} slipCount={item.slipCount} slipAt={item.slipAt} amount={item.order.total} />
                  ) : (
                    <PaymentBox key={item.trackingCode} trackingCode={item.trackingCode} phone={phone.trim()} slipCount={item.slipCount} slipAt={item.slipAt} />
                  )
                )}
                <ContactAdminBox trackingCode={item.trackingCode} status={item.status} />
              </>
            ) : (
              <>
                <h2>{item.title}</h2>
                <p>{[item.category, item.companyName].filter(Boolean).join(" · ")}</p>
                {item.issueDate && <p>ลงฉบับวันที่ {thaiDate(item.issueDate)}</p>}
                <div className="record-actions">
                  {(item.files || []).map((f, i) => (
                    <a key={i} className="text-link" href={apiUrl(f.url)} target="_blank" rel="noreferrer">
                      {f.name}{" "}
                      <span>↗</span>
                    </a>
                  ))}
                  {item.issueDate && published && (
                    <Link className="text-link" to={`/newspaper/editions/${item.issueDate}`}>
                      ดูฉบับรายวัน{" "}
                      <span>→</span>
                    </Link>
                  )}
                  {item.editionPdf ? (
                    <a className="text-link" href={apiUrl(item.editionPdf)} target="_blank" rel="noreferrer">
                      ดาวน์โหลดหนังสือพิมพ์ PDF{" "}
                      <span>↓</span>
                    </a>
                  ) : (
                    item.issueDate && <small>ดาวน์โหลด PDF หนังสือพิมพ์ฉบับนี้ได้ตั้งแต่วันที่ {thaiDate(item.editionDownloadFrom)} (วันถัดจากวันลงฉบับ)</small>
                  )}
                </div>
              </>
            )}
          </article>
        </div>
      )}
    </main>
  );
}

export default StatusPage;
